/**
 * Platform Analytics Dashboard
 *
 * Admin dashboard for viewing platform-wide analytics and metrics.
 * Displays user stats, experience metrics, booking trends, revenue analytics,
 * and guide performance data.
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-147 - Platform Analytics & Reporting (34 SP)
 *
 * Features:
 * - Platform overview cards (users, experiences, bookings, revenue, reviews)
 * - Top performers lists (revenue, bookings, recently active)
 * - Guide performance table with filters
 * - Manual refresh with last updated timestamp
 * - Redis-cached data with hourly auto-refresh
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Users as _Users,
  Briefcase,
  Calendar,
  DollarSign,
  Star as _Star,
  RefreshCw,
  TrendingUp as _TrendingUp,
  Clock,
  Award,
  Activity,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import usePlatformAnalytics from '../../hooks/admin/usePlatformAnalytics';

export default function PlatformAnalyticsDashboard() {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const {
    overview,
    topRevenue,
    topBookings,
    recentlyActive,
    lastRefresh: _lastRefresh,
    loading,
    error,
    actionLoading,
    refreshAnalytics,
    fetchAllData
  } = usePlatformAnalytics(true);

  const [refreshing, setRefreshing] = useState(false);

  /**
   * Handle navigation
   */
  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  /**
   * Handle manual refresh
   */
  const handleRefresh = async () => {
    setRefreshing(true);
    const result = await refreshAnalytics();

    if (result.success) {
      // Fetch all data again after refresh
      await fetchAllData();
    }

    setRefreshing(false);
  };

  /**
   * Format currency
   */
  const formatCurrency = (value) => {
    if (!value) {return '$0';}
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  /**
   * Format number with commas
   */
  const formatNumber = (value) => {
    if (!value) {return '0';}
    return new Intl.NumberFormat('en-US').format(value);
  };

  /**
   * Format date/time
   */
  const _formatDateTime = (dateString) => {
    if (!dateString) {return 'Never';}
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Get growth indicator
   */
  const _getGrowthIndicator = (current, previous) => {
    if (!previous || previous === 0) {return null;}

    const percentChange = ((current - previous) / previous) * 100;
    const isPositive = percentChange > 0;

    return (
      <span className={`inline-flex items-center text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? <ArrowUp className="w-4 h-4 mr-1" /> : <ArrowDown className="w-4 h-4 mr-1" />}
        {Math.abs(percentChange).toFixed(1)}%
      </span>
    );
  };

  if (loading && !overview) {
    return (
      <DashboardLayout
        userRole="admin"
        activeNavId="analytics"
        onNavigate={handleNavigate}
        user={{ name: authUser?.fullName || authUser?.firstName || 'Admin', email: authUser?.email || '', avatar: authUser?.profilePicture || null }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '2px solid var(--color-border)',
            borderTopColor: 'var(--color-primary)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error && !overview) {
    return (
      <DashboardLayout
        userRole="admin"
        activeNavId="analytics"
        onNavigate={handleNavigate}
        user={{ name: authUser?.fullName || authUser?.firstName || 'Admin', email: authUser?.email || '', avatar: authUser?.profilePicture || null }}
      >
        <div style={{
          backgroundColor: 'var(--color-error-light)',
          border: '1px solid var(--color-error)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-4)'
        }}>
          <p style={{ color: 'var(--color-error)' }}>Error loading analytics: {error}</p>
          <button
            onClick={fetchAllData}
            style={{
              marginTop: 'var(--space-2)',
              padding: 'var(--space-2) var(--space-4)',
              backgroundColor: 'var(--color-error)',
              color: 'var(--color-white)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="analytics"
      onNavigate={handleNavigate}
      user={{ name: authUser?.fullName || authUser?.firstName || 'Admin', email: authUser?.email || '', avatar: authUser?.profilePicture || null }}
    >
      {/* Page Header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
          <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-primary)', margin: 0 }}>
            Platform Analytics
          </h1>
          <button
            onClick={handleRefresh}
            disabled={refreshing || actionLoading}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: 'var(--space-2) var(--space-4)',
              border: 'none',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-white)',
              backgroundColor: (refreshing || actionLoading) ? 'var(--color-gray-300)' : 'var(--color-primary)',
              cursor: (refreshing || actionLoading) ? 'not-allowed' : 'pointer',
              transition: 'all var(--transition-fast)',
              gap: 'var(--space-2)'
            }}
          >
            <RefreshCw style={{
              width: '16px',
              height: '16px',
              animation: refreshing ? 'spin 1s linear infinite' : 'none'
            }} />
            {refreshing ? 'Refreshing...' : 'Refresh Analytics'}
          </button>
        </div>
      </div>

      <div className="space-y-6">

        {/* Overview Cards */}
        {overview && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Users Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Users</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">
                    {formatNumber(overview.users?.total_users || 0)}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-gray-500">
                      {formatNumber(overview.users?.total_customers || 0)} customers
                    </span>
                    <span className="text-xs text-gray-500">•</span>
                    <span className="text-xs text-gray-500">
                      {formatNumber(overview.users?.total_guides || 0)} guides
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <_Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              {overview.users?.new_users_30d > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <span className="text-xs text-gray-500">+{formatNumber(overview.users.new_users_30d)} in 30 days</span>
                </div>
              )}
            </div>

            {/* Experiences Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Experiences</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">
                    {formatNumber(overview.experiences?.total_experiences || 0)}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-gray-500">
                      {formatNumber(overview.experiences?.active_experiences || 0)} active
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <Briefcase className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              {overview.experiences?.new_experiences_30d > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <span className="text-xs text-gray-500">+{formatNumber(overview.experiences.new_experiences_30d)} in 30 days</span>
                </div>
              )}
            </div>

            {/* Bookings Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Bookings</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">
                    {formatNumber(overview.bookings?.total_bookings || 0)}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-gray-500">
                      {formatNumber(overview.bookings?.completed_bookings || 0)} completed
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <Calendar className="w-6 h-6 text-green-600" />
                </div>
              </div>
              {overview.bookings?.bookings_30d > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <span className="text-xs text-gray-500">{formatNumber(overview.bookings.bookings_30d)} in 30 days</span>
                </div>
              )}
            </div>

            {/* Revenue Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">
                    {formatCurrency(overview.revenue?.total_revenue || 0)}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-gray-500">
                      {formatCurrency(overview.revenue?.revenue_30d || 0)} (30d)
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-yellow-100 rounded-full">
                  <DollarSign className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
              {overview.revenue?.revenue_7d > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <span className="text-xs text-gray-500">{formatCurrency(overview.revenue.revenue_7d)} in 7 days</span>
                </div>
              )}
            </div>

            {/* Reviews Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Reviews</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">
                    {formatNumber(overview.reviews?.total_reviews || 0)}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <_Star className="w-4 h-4 text-yellow-500 fill-current" />
                    <span className="text-sm font-medium text-gray-900">
                      {overview.reviews?.average_rating ? overview.reviews.average_rating.toFixed(1) : '0.0'}
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <_Star className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Top Performers Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Top Revenue */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-yellow-600" />
                <h2 className="text-lg font-semibold text-gray-900">Top Revenue</h2>
              </div>
            </div>
            <div className="p-6">
              {topRevenue && topRevenue.length > 0 ? (
                <div className="space-y-4">
                  {topRevenue.map((guide, index) => (
                    <div key={guide.guide_id} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-sm font-semibold text-gray-700">
                          #{index + 1}
                        </span>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{guide.business_name}</p>
                          <p className="text-xs text-gray-500">{formatNumber(guide.total_bookings)} bookings</p>
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-green-600">
                        {formatCurrency(guide.total_revenue)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No data available</p>
              )}
            </div>
          </div>

          {/* Top Bookings */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <_TrendingUp className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Top Bookings</h2>
              </div>
            </div>
            <div className="p-6">
              {topBookings && topBookings.length > 0 ? (
                <div className="space-y-4">
                  {topBookings.map((guide, index) => (
                    <div key={guide.guide_id} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-sm font-semibold text-gray-700">
                          #{index + 1}
                        </span>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{guide.business_name}</p>
                          <p className="text-xs text-gray-500">{formatCurrency(guide.total_revenue)} revenue</p>
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-blue-600">
                        {formatNumber(guide.total_bookings)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No data available</p>
              )}
            </div>
          </div>

          {/* Recently Active */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">Recently Active</h2>
              </div>
            </div>
            <div className="p-6">
              {recentlyActive && recentlyActive.length > 0 ? (
                <div className="space-y-4">
                  {recentlyActive.map((guide) => (
                    <div key={guide.guide_id} className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{guide.business_name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Clock className="w-3 h-3 text-gray-400" />
                          <p className="text-xs text-gray-500">
                            {guide.last_booking_date ?
                              new Date(guide.last_booking_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                              : 'No bookings'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-900">{formatNumber(guide.total_bookings)}</p>
                        <p className="text-xs text-gray-500">bookings</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No data available</p>
              )}
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900">Auto-Refresh Schedule</p>
              <p className="text-sm text-blue-700 mt-1">
                Analytics data is automatically refreshed every hour. Use the refresh button above to manually update the data.
              </p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
